import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import OnboardingStepper from '../components/OnboardingStepper';
import { normalizePlanHighlightsForBotLimit } from '../utils/planHighlights';

const MAX_COMPANIES = 5;

const NOK_FORMATTER = new Intl.NumberFormat('nb-NO', {
  style: 'currency',
  currency: 'NOK',
  maximumFractionDigits: 0,
});

const formatCurrency = (amount, currency = 'NOK') => {
  if (!Number.isFinite(amount)) return null;
  const normalizedCurrency = (currency || 'NOK').toUpperCase();
  try {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: normalizedCurrency === 'NOK' ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${normalizedCurrency}`;
  }
};

const getPrimaryStripePrice = (plan) => {
  if (!plan?.stripePrices) return null;
  const options = Object.values(plan.stripePrices);
  if (!options.length) return null;
  const withAmount = options.find((option) => Number.isFinite(Number(option.unitAmount)));
  return withAmount || options[0];
};

const formatIntervalLabel = (interval) => {
  switch ((interval || '').toLowerCase()) {
    case 'year':
    case 'annual':
      return 'år';
    case 'week':
      return 'uke';
    case 'day':
      return 'dag';
    default:
      return 'mnd';
  }
};

const resolveStripeAmount = (price) => {
  if (!price) return null;
  const direct = Number(price.unitAmount ?? price.amount ?? NaN);
  if (Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  const minorCandidates = [
    price.unit_amount,
    price.unitAmountMinor,
    price.unit_amount_decimal,
    price.amount_minor,
  ];
  for (const candidate of minorCandidates) {
    const value = Number(candidate ?? NaN);
    if (Number.isFinite(value) && value > 0) {
      return value / 100;
    }
  }
  return null;
};

const formatStripePriceLabel = (price) => {
  const amount = resolveStripeAmount(price);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  const currency = price?.currency || 'NOK';
  const formatted = formatCurrency(amount, currency);
  if (!formatted) return null;
  const intervalLabel = formatIntervalLabel(price?.recurring?.interval || price?.interval);
  return `${formatted} / ${intervalLabel}`;
};

const normalizePlanRole = (value) => {
  if (!value) return null;
  const normalized = value.toString().trim().toLowerCase();
  if (!normalized) return null;
  if (['gratis', 'free', 'free_tier', 'freetier'].includes(normalized)) return 'free';
  if (['premium', 'pro'].includes(normalized)) return 'premium';
  if (['paid', 'betalt'].includes(normalized)) return 'paid';
  if (
    [
      'payasyougo',
      'pay_as_you_go',
      'pay-as-you-go',
      'payg',
      'usage',
      'metered',
      'forbruk',
      'forbruksbasert',
    ].includes(normalized)
  ) {
    return 'payasyougo';
  }
  if (['enterprise', 'bedrift', 'business'].includes(normalized)) return 'enterprise';
  return normalized;
};

const resolvePlanRole = (plan) => normalizePlanRole(
  plan?.features?.planRole
    || plan?.metadata?.planRole
    || plan?.metadata?.plan_role,
);

const resolveBillingMode = (plan) => {
  const billingMode =
    plan?.features?.billingMode
    || plan?.metadata?.billingMode
    || plan?.metadata?.billing_mode;
  return typeof billingMode === 'string' ? billingMode.toLowerCase() : null;
};

const formatPrice = (plan) => {
  if (!plan) return 'Gratis';
  const displayPrice = plan.metadata?.displayPrice || plan.displayPrice;
  if (displayPrice) return displayPrice;
  const planRole = resolvePlanRole(plan);
  if (planRole === 'payasyougo') return 'Betal etter forbruk';
  if (planRole === 'free') return 'Gratis';
  const stripePrice = formatStripePriceLabel(getPrimaryStripePrice(plan));
  if (stripePrice) return stripePrice;
  const monthly = Number(plan.monthlyPrice ?? plan.priceMonthly ?? NaN);
  if (Number.isFinite(monthly) && monthly > 0) {
    return `${NOK_FORMATTER.format(monthly)} / mnd`;
  }
  if (Number.isFinite(monthly) && monthly === 0) {
    return `${NOK_FORMATTER.format(0)} / mnd`;
  }
  if (resolveBillingMode(plan) === 'metered') {
    return `${NOK_FORMATTER.format(0)} / mnd`;
  }
  return 'Gratis';
};

const CompanyOverviewPage = () => {
  const {
    companies,
    companiesLoading,
    companiesError,
    companiesInitialized,
    selectedCompanyId,
    selectCompany,
    createCompany,
    user,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from?.pathname || '/';
  const [newCompanyName, setNewCompanyName] = useState('');
  const [organizationNumber, setOrganizationNumber] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectingCompanyId, setSelectingCompanyId] = useState(null);
  const [formError, setFormError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [planOptions, setPlanOptions] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);
  const [selectedPlanCode, setSelectedPlanCode] = useState(null);
  const [pendingInvite, setPendingInvite] = useState(null);
  const [pendingInviteLoading, setPendingInviteLoading] = useState(false);
  const [pendingInviteError, setPendingInviteError] = useState(null);

  const isOnboarding = useMemo(
    () => Boolean(companiesInitialized && companies.length === 0 && !pendingInvite),
    [companies.length, companiesInitialized, pendingInvite],
  );

  const remainingSlots = useMemo(
    () => Math.max(MAX_COMPANIES - companies.length, 0),
    [companies.length],
  );

  useEffect(() => {
    let isMounted = true;
    setPlansLoading(true);
    setPlansError(null);
    (async () => {
      try {
        const payload = await DashboardApi.getBillingPlans();
        if (!isMounted) return;
        setPlanOptions(Array.isArray(payload?.plans) ? payload.plans : []);
      } catch (error) {
        if (!isMounted) return;
        setPlansError(error.message || 'Kunne ikke hente planer.');
      } finally {
        if (isMounted) setPlansLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setPendingInviteLoading(true);
    setPendingInviteError(null);
    (async () => {
      try {
        const payload = await DashboardApi.getPendingPartnerInvite();
        if (!isMounted) return;
        if (payload?.pending && payload?.invite) {
          setPendingInvite(payload.invite);
        } else {
          setPendingInvite(null);
        }
      } catch (error) {
        if (!isMounted) return;
        setPendingInvite(null);
        setPendingInviteError(error.message || 'Kunne ikke hente invitasjonen.');
      } finally {
        if (isMounted) setPendingInviteLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!pendingInvite) return;
    navigate('/invite/company', { replace: true });
  }, [navigate, pendingInvite]);

  useEffect(() => {
    if (!pendingInvite) return;
    if (!newCompanyName.trim() && pendingInvite.companyName) {
      setNewCompanyName(pendingInvite.companyName);
    }
    if (!companyEmail.trim() && pendingInvite.companyEmail) {
      setCompanyEmail(pendingInvite.companyEmail);
    }
    if (pendingInvite.planCode) {
      setSelectedPlanCode(pendingInvite.planCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInvite]);

  useEffect(() => {
    if (selectedPlanCode || !planOptions.length) {
      return;
    }
    const defaultPlan = planOptions.find((plan) => {
      const code = String(plan.code || plan.metadata?.plan_code || plan.metadata?.planCode || '').trim().toLowerCase();
      return code === 'basic';
    }) || planOptions.find((plan) => {
      const role = resolvePlanRole(plan);
      return role === 'payasyougo';
    }) || planOptions.find((plan) => {
      const role = resolvePlanRole(plan);
      return role === 'free';
    }) || planOptions[0];
    if (defaultPlan) {
      setSelectedPlanCode(defaultPlan.code);
    }
  }, [planOptions, selectedPlanCode]);

  const invitePlanLocked = Boolean(pendingInvite?.planCode);

  const handleOpenCompany = async (companyId) => {
    setFormError(null);
    setSelectingCompanyId(companyId);
    try {
      await selectCompany(companyId);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setFormError(err.message || 'Kunne ikke åpne selskapet.');
    } finally {
      setSelectingCompanyId(null);
    }
  };

  const handleCreateCompany = async (event) => {
    event.preventDefault();
    if (!newCompanyName.trim()) {
      setFormError('Selskapsnavnet kan ikke være tomt.');
      return;
    }
    if (!organizationNumber.trim()) {
      setFormError('Organisasjonsnummer er påkrevd.');
      return;
    }
    if (!companyEmail.trim()) {
      setFormError('E-postadresse for selskapet er påkrevd.');
      return;
    }
    if (remainingSlots <= 0) {
      setFormError('Du har nådd maks antall selskaper.');
      return;
    }
    setCreating(true);
    setFormError(null);
    setCreateSuccess(null);
    try {
      const response = await createCompany({
        name: newCompanyName.trim(),
        organizationNumber: organizationNumber.trim(),
        companyEmail: companyEmail.trim(),
        planCode: isOnboarding ? undefined : selectedPlanCode || undefined,
      });
      setCreateSuccess(`Selskapet ${response?.company?.name || newCompanyName} er opprettet.`);

      if (response?.checkoutUrl) {
        window.location.href = response.checkoutUrl;
        return;
      }

      setNewCompanyName('');
      setOrganizationNumber('');
      setCompanyEmail('');
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setFormError(err.message || 'Kunne ikke opprette selskap.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="company-hub-wrapper">
      <div className="company-hub">
        <div className="company-panel account-panel">
          <header className="company-panel-header">
            <p className="company-eyebrow">Konto</p>
            <h2>{user?.displayName || user?.name || user?.email || 'Din konto'}</h2>
            {user?.email && user?.displayName && (
              <p className="company-subtitle">{user.email}</p>
            )}
          </header>
          <div className="account-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={logout}
            >
              Logg ut
            </button>
          </div>
        </div>
        <div className="company-panel">
        <header className="company-panel-header">
          <div>
            <p className="company-eyebrow">Velg selskap</p>
            <h1>Hvor vil du jobbe i dag?</h1>
            <p className="company-subtitle">
              KI Chat organiserer bots, team og fakturering per selskap. Velg et eksisterende selskap
              eller opprett et nytt for å fortsette.
            </p>
          </div>
        </header>
        {companiesError && (
          <div className="company-alert error">
            ⚠️ {companiesError}
          </div>
        )}
        {formError && (
          <div className="company-alert error">
            ⚠️ {formError}
          </div>
        )}
        {companiesLoading && !companies.length ? (
          <div className="company-empty">
            <p>Laster selskaper…</p>
          </div>
        ) : companies.length ? (
          <div className="company-list">
            {companies.map((company) => {
              const isSelected = String(company.id) === String(selectedCompanyId);
              return (
                <article
                  key={company.id}
                  className={`company-card ${isSelected ? 'selected' : ''}`}
                >
                  <div>
                    <h3>{company.name}</h3>
                    <p className="company-meta">
                      {company.membershipRoleLabel || company.membershipRole || 'Medlem'}
                      {company.planTier ? ` • Plan ${company.planTier}` : ''}
                    </p>
                    {company.botCount !== undefined && (
                      <p className="company-meta">
                        {company.botCount} bot{company.botCount === 1 ? '' : 'er'}
                      </p>
                    )}
                  </div>
                  <div className="company-card-actions">
                    <button
                      type="button"
                      className="button button-secondary"
                      onClick={() => handleOpenCompany(company.id)}
                      disabled={selectingCompanyId === company.id}
                    >
                      {selectingCompanyId === company.id
                        ? 'Åpner…'
                        : isSelected
                          ? 'Åpne selskap'
                          : 'Bytt selskap'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="company-empty">
            <p>Du er ikke medlem av noen selskaper enda. Opprett et nytt selskap for å komme i gang.</p>
          </div>
        )}
      </div>
      <div className="company-panel accent">
        <header className="company-panel-header">
          <p className="company-eyebrow">Opprett selskap</p>
          <h2>{isOnboarding ? 'Opprett selskap' : 'Start nytt selskap'}</h2>
          <p className="company-subtitle">
            Du kan ha inntil {MAX_COMPANIES} selskaper per konto. Du har {remainingSlots} igjen.
          </p>
        </header>
        {isOnboarding ? (
          <div style={{ marginBottom: 14 }}>
            <OnboardingStepper
              steps={[
                { id: 'account', label: 'Lag konto' },
                { id: 'company', label: 'Opprett selskap' },
              ]}
              activeStepId="company"
              completedStepIds={['account']}
            />
          </div>
        ) : null}
        {pendingInviteLoading && (
          <div className="company-alert">Henter invitasjon…</div>
        )}
        {pendingInviteError && (
          <div className="company-alert error">⚠️ {pendingInviteError}</div>
        )}
        {pendingInvite && (
          <div className="company-alert info">
            Du er invitert av <strong>{pendingInvite.partnerName || 'partner'}</strong>
            {pendingInvite.companyName ? <> for <strong>{pendingInvite.companyName}</strong></> : null}.
            Opprett selskapet for å fullføre invitasjonen.
          </div>
        )}
        {createSuccess && (
          <div className="company-alert success">
            {createSuccess}
          </div>
        )}
        <form className="company-form" onSubmit={handleCreateCompany}>
          <label htmlFor="newCompanyName">
            Selskapsnavn
            <input
              id="newCompanyName"
              className="input"
              type="text"
              value={newCompanyName}
              onChange={(event) => setNewCompanyName(event.target.value)}
              placeholder="Din Bedrift AS"
              disabled={creating}
              required
            />
          </label>
          <label htmlFor="organizationNumber">
            Organisasjonsnummer
            <input
              id="organizationNumber"
              className="input"
              type="text"
              value={organizationNumber}
              onChange={(event) => setOrganizationNumber(event.target.value)}
              placeholder="999888777"
              disabled={creating}
              required
            />
          </label>
          <label htmlFor="companyEmail">
            Selskapets e-postadresse
            <input
              id="companyEmail"
              className="input"
              type="email"
              value={companyEmail}
              onChange={(event) => setCompanyEmail(event.target.value)}
              placeholder="post@dinbedrift.no"
              disabled={creating}
              required
            />
          </label>

          {!isOnboarding ? (
            <div className="company-plan-section">
              <p className="company-plan-title">Velg plan</p>
              {plansError && (
                <div className="company-alert error">⚠️ {plansError}</div>
              )}
              {plansLoading ? (
                <p>Laster planer…</p>
              ) : (
                <div className="company-plan-grid">
                  {planOptions.map((plan) => {
                    const isSelected = selectedPlanCode === plan.code;
                    const highlights = Array.isArray(plan.features?.highlights)
                      ? normalizePlanHighlightsForBotLimit(plan, plan.features.highlights).slice(0, 3)
                      : [];
                    return (
                      <button
                        type="button"
                        key={plan.code}
                        className={`company-plan-card${isSelected ? ' selected' : ''}`}
                        onClick={() => {
                          if (invitePlanLocked) return;
                          setSelectedPlanCode(plan.code);
                        }}
                        disabled={creating || invitePlanLocked}
                      >
                        <div className="company-plan-card-row">
                          <div>
                            <p className="company-plan-label">{plan.name}</p>
                            <p className="company-plan-price">{formatPrice(plan)}</p>
                          </div>
                          <input
                            type="radio"
                            checked={isSelected}
                            onChange={() => {
                              if (invitePlanLocked) return;
                              setSelectedPlanCode(plan.code);
                            }}
                            disabled={creating || invitePlanLocked}
                            aria-label={`Velg ${plan.name}`}
                          />
                        </div>
                        {highlights.length > 0 && (
                          <ul className="company-plan-feature-list">
                            {highlights.map((feature) => (
                              <li key={feature}>{feature}</li>
                            ))}
                          </ul>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          <button
            type="submit"
            className="button button-primary"
            disabled={creating || remainingSlots <= 0}
          >
            {creating ? 'Oppretter…' : 'Opprett selskap'}
          </button>
        </form>
      </div>
      </div>
    </div>
  );
};

export default CompanyOverviewPage;
